import React, {Component} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {cloneDeep} from 'lodash';
import {ELEMENT_TYPE, BUTTON_STYLE, BUTTON_COLOR} from '../../constants';
import {injectIntl} from 'react-intl';
import * as tracking from '../../tracking';
import {handleChangedOptions} from '../../the_eye_util';
import CustomScrollDiv from '../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import {showModal, MODAL_TYPE} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import {buildThirdLevelNavHeader} from '../../forecast-app/shared/components/headers/header-bar/header_bar';
import SettingsPeoplePerson from '../../components/settings-people/settings_people_person';
import Info from '../../components/info';
import moment from 'moment';
import Util from '../../forecast-app/shared/util/util';
import {hasFeatureFlag} from '../../forecast-app/shared/util/FeatureUtil';
import {Link} from 'react-router-dom';
import {dispatch, EVENT_ID} from '../event_manager';
import {getSelectableProfiles, personIsClientUser, personIsVirtualUser} from '../../forecast-app/shared/util/PermissionsUtil';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../forecast-app/shared/components/headers/top-header-bar/TopHeaderBar';
import SettingsResourcesSubNav from './SettingsResourcesSubNav';

class settingsPeople extends Component {
	constructor(props) {
		super(props);

		const column = localStorage.getItem('settings-people-sort-column') || null;
		const ascending = localStorage.getItem('settings-people-sort-ascending') === 'true' || true;

		const theEyeOptions = [
			{
				name: 'created-at',
				checked: false,
				disabled: false,
				translationId: 'global_filter_field.created_at',
				sortable: true,
			},
			{
				name: 'created-by',
				checked: false,
				disabled: false,
				translationId: 'settings_details.demo_created_by',
				sortable: true,
			},
		];

		this.state = {
			searchFilterValue: '',
			showActiveMembers: true,
			showVirtualMembers: true,
			showDeactivatedMembers: false,
			sortBy: {column, ascending},
			theEyeOptions: theEyeOptions,
		};
	}

	componentDidMount() {
		document.title = 'People - Account - Forecast';
		tracking.trackPage('settings-people');

		dispatch(EVENT_ID.PAGE_LOADED, 'settings_people');
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevState.searchFilterValue !== this.state.searchFilterValue) {
			if (this.state.searchFilterValue !== '') {
				let filterValue = this.state.searchFilterValue.trim();
				const matchingPersons = this.props.viewer.company.allPersons.edges.filter(person => {
					const name = this.getPersonName(person);
					const role = person.node.role ? person.node.role.name : null;
					const skillNames = '' + person.node.skills?.edges.map(edge => edge.node?.name);
					return (
						Util.normalizedIncludes(name, filterValue) ||
						(role && Util.normalizedIncludes(role, filterValue)) ||
						Util.normalizedIncludes(skillNames, filterValue)
					);
				}).length;
				tracking.trackEvent('Search Results', {
					searchString: this.state.searchFilterValue,
					matching: matchingPersons,
				});
			}
		}
	}

	showAddTeamMemberModal() {
		showModal({
			viewer: this.props.viewer,
			type: MODAL_TYPE.ADD_TEAM_MEMBER,
			buttonLocation: 'Project Team',
		});
	}

	handleTheEyeOptionSelect(a, b, c, newOptions) {
		this.setState({theEyeOptions: handleChangedOptions(newOptions)});
	}

	handleSearchChange(value) {
		this.setState({searchFilterValue: value});
	}

	clearSearch() {
		this.setState({searchFilterValue: ''});
	}

	toggleSubSection(type, show) {
		this.setState({[type]: show});
	}

	showPermissionsInfo() {
		window.open('https://help.forecast.it/the-basics-permission-levels');
	}

	isCompanyOverUserCap() {
		const {viewer} = this.props;
		const {company} = viewer;
		const isChargebeePlan = company.subscription && company.subscription.planId.includes('chargebee');
		const users = viewer.company.allPersons.edges.map(edge => edge.node).filter(user => user.active);

		let curUsers = 0;

		if (isChargebeePlan) {
			// Normal, paid users are all non-virtual non-client users
			curUsers = users.reduce(
				(acc, user) => (!personIsVirtualUser(user) && !personIsClientUser(user) ? acc + 1 : acc),
				0
			);
		}
		const userSeats = company.subscription && company.subscription.seats;

		return curUsers != null && userSeats != null && curUsers >= userSeats;
	}

	isCompanyOverVirtualUserCap() {
		const {viewer} = this.props;
		const {company} = viewer;
		const freeVirtualUsers = company.planAddons.some(pa => pa === 'free_virtual_users');
		if (freeVirtualUsers) {
			return false;
		}
		const isChargebeeCustomer = company.isChargebeeCustomer;
		const isChargebeePlan = company.subscription && company.subscription.planId.includes('chargebee');
		const users = viewer.company.allPersons.edges.map(edge => edge.node).filter(user => user.active);

		let curVirtualUsers = isChargebeePlan ? 0 : null;

		if (isChargebeePlan) {
			curVirtualUsers = users.reduce((acc, user) => (personIsVirtualUser(user) ? acc + 1 : acc), 0);
		}

		const virtualSeats = isChargebeeCustomer ? company.subscription && company.subscription.virtualSeats : null;

		return curVirtualUsers != null && virtualSeats != null && curVirtualUsers >= virtualSeats;
	}

	getDayTableHeaders() {
		const {formatMessage, formatDate} = this.props.intl;
		return [...Array(7)].map((_, i) => (
			<th key={i} className="column-day day-width day-width-header">
				{formatDate(moment().startOf('week').add(i, 'days'), {weekday: 'short'})}
				{i === 0 ? <span className="working-hours-text">{formatMessage({id: 'common.working_hours'})}</span> : null}
			</th>
		));
	}

	setSortBy(column) {
		let ascending = true;
		if (column === this.state.sortBy.column) {
			ascending = !this.state.sortBy.ascending;
		}
		Util.localStorageSetItem('settings-people-sort-column', column);
		Util.localStorageSetItem('settings-people-sort-ascending', ascending);
		this.setState({sortBy: {column, ascending}});
	}

	syncJiraUsers(type) {
		showModal({
			type: MODAL_TYPE.JIRA_SYNC_USERS,
			companyId: this.props.viewer.company.id,
			jiraType: type,
		});
	}

	importBambooHRUsers() {
		showModal({
			type: MODAL_TYPE.BAMBOO_IMPORT_USERS,
			companyId: this.props.viewer.company.id,
		});
	}

	getTopHeader() {
		return (
			<TopHeaderBarWrapper>
				<TopHeaderBar title={this.props.intl.formatMessage({id: 'common.people'})} />
			</TopHeaderBarWrapper>
		);
	}

	getHeader() {
		const {formatMessage} = this.props.intl;
		const leftContent = [];
		const rightContent = [];

		leftContent.push({
			type: ELEMENT_TYPE.THE_EYE,
			options: this.state.theEyeOptions,
			onSelect: this.handleTheEyeOptionSelect.bind(this),
			openRight: true,
			userpilot: 'eye-selector',
		});

		//TODO only one search control in design but we have search on team and team members. Do we want to combine? Than I cant search for people and teams separately, maybe it is fine?
		rightContent.push({
			type: ELEMENT_TYPE.SEARCH_LAZY,
			value: this.state.searchFilterValue,
			onChange: this.handleSearchChange.bind(this),
		});

		if (this.props.viewer.company.jiraCloudEnabled) {
			rightContent.push({
				type: ELEMENT_TYPE.BUTTON,
				text: formatMessage({id: 'integrations.jira.import_jira_users'}),
				callback: this.syncJiraUsers.bind(this, 'cloud'),
				style: BUTTON_STYLE.OUTLINE,
				color: BUTTON_COLOR.PURPLE,
			});
		}

		if (this.props.viewer.company.jiraServerEnabled) {
			rightContent.push({
				type: ELEMENT_TYPE.BUTTON,
				text: formatMessage({id: 'integrations.jira.import_jira_users'}),
				callback: this.syncJiraUsers.bind(this, 'server'),
				style: BUTTON_STYLE.OUTLINE,
				color: BUTTON_COLOR.PURPLE,
			});
		}

		if (this.props.viewer.company.bambooHREnabled) {
			rightContent.push({
				type: ELEMENT_TYPE.BUTTON,
				text: formatMessage({id: 'integrations.bamboohr.import_users'}),
				callback: this.importBambooHRUsers.bind(this),
				style: BUTTON_STYLE.OUTLINE,
				color: BUTTON_COLOR.PURPLE,
			});
		}

		rightContent.push({
			type: ELEMENT_TYPE.BUTTON,
			text: formatMessage({id: 'settings_people.invite_new_team_member'}),
			callback: this.showAddTeamMemberModal.bind(this),
			style: BUTTON_STYLE.OUTLINE,
			color: BUTTON_COLOR.PURPLE,
			dataCy: 'add-team-member-button',
		});

		return buildThirdLevelNavHeader(leftContent, rightContent);
	}

	getPersonName(person) {
		return (person.node.firstName || '') + ' ' + (person.node.lastName || '');
	}

	render() {
		const {formatMessage} = this.props.intl;
		const {viewer} = this.props;

		const sortedRoles = cloneDeep(viewer.company.roles.edges).sort((a, b) =>
			Util.sortAlphabetically(a.node.name, b.node.name)
		);
		const sortedDepartments = cloneDeep(viewer.company.departments.edges).sort((a, b) =>
			Util.sortAlphabetically(a.node.name, b.node.name)
		);
		const sortedProfiles = getSelectableProfiles(viewer.company.profiles.edges);
		const sortedProfileIds = sortedProfiles.map(profile => profile.id);
		const profileOptions = sortedProfiles.map(profile => {
			return {label: profile.name, value: profile.id, profile: profile};
		});

		let allPersons = cloneDeep(viewer.company.allPersons.edges).sort((a, b) =>
			Util.sortAlphabetically(this.getPersonName(a), this.getPersonName(b))
		);

		if (this.state.searchFilterValue !== '') {
			let filterValue = this.state.searchFilterValue.trim();
			allPersons = allPersons.filter(person => {
				const name = this.getPersonName(person);
				const role = person.node.role ? person.node.role.name : null;
				const permission = profileOptions.find(p => p.value === person.node.profileId)?.label;
				const skillNames = '' + person.node.skills?.edges.map(edge => edge.node?.name);
				return (
					Util.normalizedIncludes(name, filterValue) ||
					(role && Util.normalizedIncludes(role, filterValue)) ||
					Util.normalizedIncludes(permission, filterValue) ||
					Util.normalizedIncludes(skillNames, filterValue)
				);
			});
		}
		const sortFunction = (a, b) => {
			const {column} = this.state.sortBy;
			const multiplier = this.state.sortBy.ascending ? 1 : -1;
			let sortFunc = () => 1;
			if (column === 'name') {
				sortFunc = obj => obj.node.fullName.toLowerCase();
			} else if (column === 'role') {
				sortFunc = obj => (obj.node.role ? obj.node.role.name.toLowerCase() : '');
			} else if (column === 'department') {
				sortFunc = obj => (obj.node.department ? obj.node.department.name.toLowerCase() : '');
			} else if (column === 'permission') {
				sortFunc = obj => sortedProfileIds.indexOf(obj.node.profileId);
			} else if (column === 'startDate') {
				sortFunc = obj => (obj.node.startDate ? obj.node.startDate : 0);
			} else if (column === 'endDate') {
				sortFunc = obj => (obj.node.endDate ? obj.node.endDate : 0);
			}
			const sortA = sortFunc(a);
			const sortB = sortFunc(b);
			const res = sortA > sortB ? 1 : sortA === sortB ? 0 : -1;
			return multiplier * res;
		};
		allPersons.sort(sortFunction);

		const activated = allPersons.map(p => p.node).filter(person => person.active && !personIsVirtualUser(person));
		const virtual = allPersons.map(p => p.node).filter(person => person.active && personIsVirtualUser(person));

		const integrationsEnabled =
			this.props.viewer.company.harvestEnabled ||
			this.props.viewer.company.vstsEnabled ||
			this.props.viewer.company.githubEnabled ||
			this.props.viewer.company.gitlabEnabled ||
			this.props.viewer.company.gitlabServerEnabled ||
			this.props.viewer.company.jiraCloudEnabled ||
			this.props.viewer.company.jiraServerEnabled ||
			this.props.viewer.company.timelogEnabled ||
			this.props.viewer.company.unit4Enabled ||
			this.props.viewer.asanaEnabled ||
			this.props.viewer.company.teamsEnabled ||
			this.props.viewer.company.bambooHREnabled;

		const deactivated = allPersons.map(p => p.node).filter(p => !p.active);
		const sortByColumn = this.state.sortBy.column;

		const isChargebeeCustomer = this.props.viewer.company.isChargebeeCustomer;
		const freeVirtualUsers = this.props.viewer.company.planAddons.some(pa => pa === 'free_virtual_users');
		const numSeats = this.props.viewer.company.subscription && this.props.viewer.company.subscription.seats;
		const numVirtualSeats = this.props.viewer.company.subscription && this.props.viewer.company.subscription.virtualSeats;

		const hasFinancial = Util.hasFinance(this.props.viewer.company.modules);

		return (
			<div className={'section-content settings-app settings-people'} data-cy={'settings-people-page'}>
				{this.props.children}
				{hasFeatureFlag('skills') ? <SettingsResourcesSubNav viewer={this.props.viewer} /> : this.getTopHeader()}
				{this.getHeader()}
				<CustomScrollDiv>
					<div className={'section-body'}>
						<div className="inner">
							<fieldset className="people">
								<div className={'top-container' + (this.state.showActiveMembers ? '' : ' collapsed')}>
									<div data-userpilot={'people-users-header'}>
										<h2>
											{formatMessage({id: 'settings_subscription.users'})}{' '}
											<span data-cy="number-of-users">
												(
												{isChargebeeCustomer
													? formatMessage(
															{id: 'settings_people.user_seats_info'},
															{amount: activated.length, total: numSeats}
													  )
													: activated.length}
												)
											</span>
										</h2>
									</div>
									<Info
										infoMessage={formatMessage({
											id: 'settings_people.active_description',
										})}
										useNewIcon={true}
										small={true}
									/>
									<div className="controls">
										<div
											className={
												'expand-control' + (this.state.showActiveMembers ? ' expand' : ' collapse')
											}
											onClick={this.toggleSubSection.bind(
												this,
												'showActiveMembers',
												!this.state.showActiveMembers
											)}
										/>
									</div>
									{isChargebeeCustomer ? (
										<Link className={'manage-subscription-link'} to={'/settings/subscription'}>
											{formatMessage({id: 'team_modal.manage_subscription'})}
										</Link>
									) : null}
								</div>
								{this.state.showActiveMembers ? (
									<table>
										<colgroup>
											<col className="column-person" />
											<col className="column-role" />
											{Util.hasDepartments(this.props.viewer.company.modules) ? (
												<col className="column-department" />
											) : null}
											<col className="column-permissions" />
											<col className="column-cost" />
											<col className="column-date" />
											<col className="column-date" />
											{this.state.theEyeOptions.map(option =>
												option.checked ? <col className={'column-' + option.name} /> : null
											)}
											{integrationsEnabled ? <col className="column-controls" /> : null}
											<col className="column-controls" />
										</colgroup>
										<thead>
											<tr>
												<th
													className={`column-person person-width sortable ${
														sortByColumn === 'name'
															? this.state.sortBy.ascending
																? 'ascending'
																: 'descending'
															: null
													}`}
													onClick={this.setSortBy.bind(this, 'name')}
												>
													{formatMessage({id: 'common.name'})}
													<span>&nbsp;&nbsp;</span>
												</th>
												<th
													className={`column-role role-width role-width-header sortable ${
														sortByColumn === 'role'
															? this.state.sortBy.ascending
																? 'ascending'
																: 'descending'
															: null
													}`}
													onClick={this.setSortBy.bind(this, 'role')}
												>
													{formatMessage({id: 'common.role'})}
													<span>&nbsp;&nbsp;</span>
												</th>
												{Util.hasSkills() && hasFeatureFlag('skills') && (
													<th>{formatMessage({id: 'common.skills'})}</th>
												)}
												{Util.hasDepartments(this.props.viewer.company.modules) ? (
													<th
														className={`column-department department-width department-width-header sortable ${
															sortByColumn === 'department'
																? this.state.sortBy.ascending
																	? 'ascending'
																	: 'descending'
																: null
														}`}
														onClick={this.setSortBy.bind(this, 'department')}
													>
														{formatMessage({id: 'common.department'})}
														<span>&nbsp;&nbsp;</span>
													</th>
												) : null}
												<th
													className={`column-permissions permissions-width permissions-width-header sortable ${
														sortByColumn === 'permission'
															? this.state.sortBy.ascending
																? 'ascending'
																: 'descending'
															: null
													}`}
													onClick={this.setSortBy.bind(this, 'permission')}
												>
													{formatMessage({id: 'settings.permission_profile'})}
													<span>&nbsp;&nbsp;</span>
												</th>
												{hasFinancial ? (
													<th className="column-cost cost-width cost-width-header">
														{formatMessage({id: 'settings_people.internal_hourly_cost'})}
													</th>
												) : null}
												<th
													className={`column-date date-width date-width-header sortable ${
														sortByColumn === 'startDate'
															? this.state.sortBy.ascending
																? 'ascending'
																: 'descending'
															: null
													}`}
													onClick={this.setSortBy.bind(this, 'startDate')}
													data-userpilot={'people-users-start-date'}
												>
													{formatMessage({id: 'common.start_date'})}
													<span>&nbsp;&nbsp;</span>
												</th>
												<th
													className={`column-date date-width date-width-header sortable ${
														sortByColumn === 'endDate'
															? this.state.sortBy.ascending
																? 'ascending'
																: 'descending'
															: null
													}`}
													onClick={this.setSortBy.bind(this, 'endDate')}
												>
													{formatMessage({id: 'common.end_date'})}
													<span>&nbsp;&nbsp;</span>
												</th>
												{this.state.theEyeOptions.map(option =>
													option.checked ? (
														<th
															className={
																'column-' +
																option.name +
																(option.sortable &&
																	` sortable ${
																		sortByColumn === option.name
																			? this.state.sortBy.ascending
																				? 'ascending'
																				: 'descending'
																			: null
																	}`)
															}
															onClick={this.setSortBy.bind(this, option.name)}
														>
															{formatMessage({id: option.translationId})}
															{option.sortable && <span>&nbsp;&nbsp;</span>}
														</th>
													) : null
												)}
												{integrationsEnabled ? (
													<th className="column-controls">
														{formatMessage({id: 'settings_account.section-integrations'})}
													</th>
												) : null}
												<th className="column-controls controls-width">&nbsp;</th>
											</tr>
										</thead>

										<tbody>
											{activated.map((person, index) => (
												<SettingsPeoplePerson
													firstUser={index === 0}
													isCompanyOverUserCap={this.isCompanyOverUserCap.bind(this)}
													isCompanyOverVirtualUserCap={this.isCompanyOverVirtualUserCap.bind(this)}
													hasFinancial={hasFinancial}
													key={person.id}
													person={person}
													viewer={viewer}
													integrationsEnabled={integrationsEnabled}
													theEyeOptions={this.state.theEyeOptions}
													roles={sortedRoles}
													departments={sortedDepartments}
													profiles={profileOptions}
												/>
											))}
										</tbody>
									</table>
								) : null}
							</fieldset>

							<fieldset className="people virtual-users">
								<div className={'top-container' + (this.state.showVirtualMembers ? '' : ' collapsed')}>
									<div data-userpilot={'people-virtual-users-header'}>
										<h2>
											{formatMessage({id: 'settings_subscription.virtual_users'})}{' '}
											<span>
												(
												{isChargebeeCustomer && !freeVirtualUsers
													? formatMessage(
															{id: 'settings_people.virtual_seats_info'},
															{amount: virtual.length, total: numVirtualSeats}
													  )
													: virtual.length}
												)
											</span>
										</h2>
									</div>
									<Info
										infoMessage={formatMessage({
											id: 'settings_people.virtual_description',
										})}
										useNewIcon={true}
										small={true}
									/>
									<div className="controls">
										<div
											className={
												'expand-control' + (this.state.showVirtualMembers ? ' expand' : ' collapse')
											}
											onClick={this.toggleSubSection.bind(
												this,
												'showVirtualMembers',
												!this.state.showVirtualMembers
											)}
										/>
									</div>
									{isChargebeeCustomer && !freeVirtualUsers ? (
										<Link className={'manage-subscription-link'} to={'/settings/subscription'}>
											{formatMessage({id: 'team_modal.manage_subscription'})}
										</Link>
									) : null}
								</div>
								{this.state.showVirtualMembers ? (
									<table>
										<colgroup>
											<col className="column-person" />
											<col className="column-role" />
											{Util.hasDepartments() ? <col className="column-department" /> : null}
											<col className="column-permissions" />
											<col className="column-cost" />
											<col className="column-date" />
											<col className="column-date" />
											{integrationsEnabled ? <col className="column-controls" /> : null}
											<col className="column-controls" />
										</colgroup>
										<thead>
											<tr>
												<th className={'column-person person-width'}>
													{formatMessage({id: 'common.name'})}
												</th>
												<th className={'column-role role-width role-width-header'}>
													{formatMessage({id: 'common.role'})}
												</th>
												{Util.hasSkills() && hasFeatureFlag('skills') && (
													<th>{formatMessage({id: 'common.skills'})}</th>
												)}
												{Util.hasDepartments() ? (
													<th
														className={'column-department department-width department-width-header'}
													>
														{formatMessage({id: 'common.department'})}
													</th>
												) : null}
												<th className={'column-permissions permissions-width permissions-width-header'}>
													{formatMessage({id: 'common.permission'})}
												</th>
												{hasFinancial ? (
													<th className="column-cost cost-width cost-width-header">
														{formatMessage({id: 'settings_people.internal_hourly_cost'})}
													</th>
												) : null}
												<th className={'column-date date-width date-width-header'}>
													{formatMessage({id: 'common.start_date'})}
												</th>
												<th className={'column-date date-width date-width-header'}>
													{formatMessage({id: 'common.end_date'})}
												</th>

												{this.state.theEyeOptions.map(option =>
													option.checked ? (
														<th className={'column-' + option.name}>
															{formatMessage({id: option.translationId})}
														</th>
													) : null
												)}
												{integrationsEnabled ? (
													<th className="column-controls">
														{formatMessage({id: 'settings_account.section-integrations'})}
													</th>
												) : null}
												<th className="column-controls controls-width">&nbsp;</th>
											</tr>
										</thead>

										<tbody>
											{virtual.map(person => (
												<SettingsPeoplePerson
													isCompanyOverUserCap={this.isCompanyOverUserCap.bind(this)}
													isCompanyOverVirtualUserCap={this.isCompanyOverVirtualUserCap.bind(this)}
													hasFinancial={hasFinancial}
													key={person.id}
													person={person}
													viewer={viewer}
													integrationsEnabled={integrationsEnabled}
													theEyeOptions={this.state.theEyeOptions}
													roles={sortedRoles}
													departments={sortedDepartments}
													profiles={profileOptions}
												/>
											))}
										</tbody>
									</table>
								) : null}
							</fieldset>

							<fieldset className="people deactivated">
								<div className={'top-container' + (this.state.showDeactivatedMembers ? '' : ' collapsed')}>
									<div data-userpilot={'people-deactivated-users-header'}>
										<h2>
											{formatMessage({id: 'team.deactivated-group-title'})}{' '}
											<span>({deactivated.length})</span>
										</h2>
									</div>
									<Info
										infoMessage={formatMessage({
											id: 'settings_people.deactivated_description',
										})}
										useNewIcon={true}
										small={true}
									/>
									<div className="controls">
										<div
											className={
												'expand-control' + (this.state.showDeactivatedMembers ? ' expand' : ' collapse')
											}
											onClick={this.toggleSubSection.bind(
												this,
												'showDeactivatedMembers',
												!this.state.showDeactivatedMembers
											)}
										/>
									</div>
								</div>
								{this.state.showDeactivatedMembers ? (
									<table className="deactivated-people-table">
										<colgroup>
											<col className="column-person" />
											<col className="column-role" />
											{Util.hasDepartments() ? <col className="column-department" /> : null}
											<col className="column-permissions" />
											<col className="column-cost" />
											<col className="column-date" />
											<col className="column-date" />
											{integrationsEnabled ? <col className="column-controls" /> : null}
											<col className="column-controls" />
										</colgroup>
										<thead>
											<tr>
												<th className={'column-person person-width'}>
													{formatMessage({id: 'common.name'})}
												</th>
												<th className={'column-role role-width role-width-header'}>
													{formatMessage({id: 'common.role'})}
												</th>
												{Util.hasSkills() && hasFeatureFlag('skills') && (
													<th>{formatMessage({id: 'common.skills'})}</th>
												)}
												{Util.hasDepartments() ? (
													<th
														className={'column-department department-width department-width-header'}
													>
														{formatMessage({id: 'common.department'})}
													</th>
												) : null}
												<th className={'column-permissions permissions-width permissions-width-header'}>
													{formatMessage({id: 'common.permission'})}
												</th>
												{hasFinancial ? (
													<th className="column-cost cost-width cost-width-header">
														{formatMessage({id: 'settings_people.internal_hourly_cost'})}
													</th>
												) : null}
												<th className={'column-date date-width date-width-header'}>
													{formatMessage({id: 'common.start_date'})}
												</th>
												<th className={'column-date date-width date-width-header'}>
													{formatMessage({id: 'common.end_date'})}
												</th>
												{integrationsEnabled ? (
													<th
														className={
															'column-controls controls-width deactivated-controls-width harvest'
														}
													>
														{formatMessage({id: 'settings_account.section-integrations'})}
													</th>
												) : null}
												<th className="column-controls controls-width">&nbsp;</th>
											</tr>
										</thead>

										<tbody>
											{deactivated.map(person => (
												<SettingsPeoplePerson
													isCompanyOverUserCap={this.isCompanyOverUserCap.bind(this)}
													isCompanyOverVirtualUserCap={this.isCompanyOverVirtualUserCap.bind(this)}
													deactivated={true}
													key={person.id}
													person={person}
													viewer={viewer}
													integrationsEnabled={integrationsEnabled}
													roles={sortedRoles}
													departments={sortedDepartments}
													hasFinancial={hasFinancial}
													profiles={profileOptions}
												/>
											))}
										</tbody>
									</table>
								) : null}
							</fieldset>
						</div>
					</div>
				</CustomScrollDiv>
			</div>
		);
	}
}

settingsPeople.propTypes = {};

const settingsPeopleQuery = graphql`
	query settingsPeople_Query {
		viewer {
			actualPersonId
			component(name: "settings_people")
			...settingsPeople_viewer
		}
	}
`;

graphql`
	fragment settingsPeople_weekdays on Person {
		monday
		tuesday
		wednesday
		thursday
		friday
		saturday
		sunday
	}
`;

export {settingsPeopleQuery};

export default injectIntl(
	createFragmentContainer(settingsPeople, {
		viewer: graphql`
			fragment settingsPeople_viewer on Viewer {
				...SettingsResourcesSubNav_viewer
				id
				actualPersonId
				language
				asanaEnabled
				availableFeatureFlags {
					key
				}
				company {
					id
					isChargebeeCustomer
					planAddons
					currency
					harvestEnabled
					vstsEnabled
					githubEnabled
					gitlabEnabled
					gitlabServerEnabled
					jiraCloudEnabled
					jiraServerEnabled
					timelogEnabled
					unit4Enabled
					teamsEnabled
					bambooHREnabled
					userSeats
					virtualSeats
					tier
					modules {
						moduleType
					}
					contactPerson {
						id
					}
					profiles(first: 10000) @connection(key: "Company_profiles", filters: []) {
						edges {
							node {
								id
								name
								canBeEdited
								personsCount
								permissions
								disabled
							}
						}
					}
					roles(first: 1000) {
						edges {
							node {
								id
								name
							}
						}
					}
					departments(first: 1000) {
						edges {
							node {
								id
								name
							}
						}
					}
					subscription {
						planId
						seats
						virtualSeats
					}
					allPersons(first: 10000, excludeClientUsers: true) @connection(key: "Company_allPersons", filters: []) {
						edges {
							node {
								id
								createdAt
								createdBy
								harvestUser
								economicUser
								initials
								timeLogUser {
									integrationUserId
								}
								gitlabUser {
									integrationUserId
								}
								gitlabServerUser {
									integrationUserId
								}
								githubUser {
									integrationUserId
								}
								jiraServerUser
								jiraCloudUser
								asanaUser
								vstsUser
								unit4User
								msTeamsId
								bambooHRId
								firstName
								lastName
								fullName
								email
								startDate
								endDate
								profilePictureId
								profilePictureDefaultId
								role {
									id
									name
								}
								skills {
									edges {
										node {
											id
											name
										}
									}
								}
								profiles(first: 10000) {
									edges {
										node {
											id
										}
									}
								}
								profileId
								permissions
								userType # deprecated with release of new permissions
								active
								invited
								isViewer
								...settingsPeople_weekdays @relay(mask: false)
								costPeriods(first: 10000) @connection(key: "Person_costPeriods", filters: []) {
									edges {
										node {
											id
											startDay
											startMonth
											startYear
											cost
										}
									}
								}
								department {
									id
									name
								}
							}
						}
					}
				}
			}
		`,
	})
);
